import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { expressURL, authFetch, getAuthHeaders } from '../utils/networkUtils';
import { markCookieSession } from '../utils/authToken.ts';
import { useUser } from './UserContext.tsx';
import {
  IdentityBundle,
  WrappedPrivateKey,
  WrappedThreadKey,
  importPrivateKey,
  rewrapPrivateJwk,
  unwrapPrivateKeyWithWrapKey,
  unwrapThreadKey,
  wrapPrivateKeyWithWrapKey,
  wrapThreadKeyForUser
} from '../crypto/identity.ts';
import { generateThreadKeyBytes } from '../crypto/aes.ts';
import { base64ToBytes, base64UrlToBytes, bytesToBase64 } from '../crypto/bytes.ts';
import {
  DerivedPasswordKeys,
  PasswordKdfPublic,
  derivePasswordKeys,
  importWrapKey,
  newPasswordKdf
} from '../crypto/passwordSplit.ts';
import {
  ADULT_VAULT_CHANGED_EVENT,
  clearPersistedAdultIdentityJwk,
  exportAdultVaultFromIdentity,
  installAdultVaultKey,
  persistAdultIdentityJwk,
  readStoredAdultIdentityJwk,
} from '../crypto/adultVault.ts';
import {
  generateAccessRoot,
  generatePeriodAccessKey,
  isoWeekPeriodId,
  unwrapAccessRootForDevice,
  unwrapPeriodAccessKey,
  validateIsoWeekPeriodId,
  wrapAccessRootForDevice,
  wrapPeriodAccessKey,
  wrapThreadEpochForPeriod,
  type AccessRootDeviceEnvelope,
  type AccessThreadEpochWrap
} from '../crypto/access.ts';
import { ROOM_TYPES } from './threadRoomType.ts';

export type SssMetadata = {
  n: number;
  k: number;
  holderLabels: string[];
};

export type UserCryptoMaterial = {
  enabled: boolean;
  publicKeyJwk: JsonWebKey;
  wrappedPrivateKey: WrappedPrivateKey;
  sss?: SssMetadata;
};

type CryptoStatus = 'idle' | 'loading' | 'none' | 'locked' | 'unlocked';

type CryptoContextType = {
  status: CryptoStatus;
  material: UserCryptoMaterial | null;
  error: string | null;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  unlockWithPassword: (password: string) => Promise<void>;
  lock: () => void;
  setupEncryption: () => Promise<void>;
  changeLoginPassword: (currentPassword: string, newPassword: string) => Promise<void>;
  wrapNewThreadKeys: (
    members: { id: string; cryptoPublicKey?: JsonWebKey | null }[],
    options?: { roomType?: string }
  ) => Promise<{
    threadKeyBytes: Uint8Array;
    wrappedKeys: WrappedThreadKey[];
    accessPeriodWraps: AccessThreadEpochWrap[];
  }>;
  wrapThreadKeyForMembers: (
    threadKeyBytes: Uint8Array,
    members: { id: string; cryptoPublicKey?: JsonWebKey | null }[]
  ) => Promise<WrappedThreadKey[]>;
  unwrapThreadKeyForSelf: (wrappedKeys: WrappedThreadKey[]) => Promise<Uint8Array>;
  exportAdultVaultKey: (ownerUserId: string) => Promise<Uint8Array>;
  installRecoveredAdultVault: (ownerUserId: string, vaultKey: Uint8Array) => Promise<void>;
  exportAccessRootForCustody: () => Promise<Uint8Array>;
};

const STORAGE_WRAP = 'farfar-wrap-raw';
const STORAGE_ACCESS_ROOT = 'farfar-access-root';

function readStored(key: string): string | null {
  return localStorage.getItem(key) ?? sessionStorage.getItem(key);
}

function writeStored(key: string, value: string) {
  localStorage.setItem(key, value);
  sessionStorage.removeItem(key);
}

function removeStored(key: string) {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
}

export function hasPersistedWrapKey(): boolean {
  return Boolean(readStored(STORAGE_WRAP));
}

const CryptoContext = createContext<CryptoContextType>({
  status: 'idle',
  material: null,
  error: null,
  loginWithPassword: async () => {},
  unlockWithPassword: async () => {},
  lock: () => {},
  setupEncryption: async () => {},
  changeLoginPassword: async () => {},
  wrapNewThreadKeys: async () => ({ threadKeyBytes: new Uint8Array(), wrappedKeys: [], accessPeriodWraps: [] }),
  wrapThreadKeyForMembers: async () => [],
  unwrapThreadKeyForSelf: async () => new Uint8Array(),
  exportAdultVaultKey: async () => {
    throw new Error('Adult-chat keys are not available.');
  },
  installRecoveredAdultVault: async () => {
    throw new Error('Adult-chat keys cannot be installed.');
  },
  exportAccessRootForCustody: async () => new Uint8Array()
});

export const useCrypto = () => useContext(CryptoContext);

function persistSession(userId: string, privateKeyJwk: JsonWebKey, wrapKeyRaw?: Uint8Array | null) {
  persistAdultIdentityJwk(userId, privateKeyJwk, { notify: false });
  if (wrapKeyRaw) {
    writeStored(STORAGE_WRAP, JSON.stringify({ userId, wrapKeyRaw: bytesToBase64(wrapKeyRaw) }));
  }
}

function readSessionJwk(userId: string): JsonWebKey | null {
  return readStoredAdultIdentityJwk(userId);
}

function readSessionWrap(userId: string): Uint8Array | null {
  try {
    const raw = readStored(STORAGE_WRAP);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.userId !== userId || !parsed.wrapKeyRaw) return null;
    return base64ToBytes(parsed.wrapKeyRaw);
  } catch {
    return null;
  }
}

function clearSession() {
  clearPersistedAdultIdentityJwk();
  removeStored(STORAGE_WRAP);
}

async function fetchPrelogin(email: string): Promise<{ v: 0 } | PasswordKdfPublic> {
  const response = await axios.post(`${expressURL()}/auth/prelogin`, { email });
  return response.data;
}

function readStoredAccessRoot(userId: string): AccessRootDeviceEnvelope | null {
  try {
    const raw = readStored(STORAGE_ACCESS_ROOT);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.userId !== userId || !parsed.envelope) return null;
    return parsed.envelope as AccessRootDeviceEnvelope;
  } catch {
    return null;
  }
}

function writeStoredAccessRoot(userId: string, envelope: AccessRootDeviceEnvelope) {
  writeStored(STORAGE_ACCESS_ROOT, JSON.stringify({ userId, envelope }));
}

async function persistPeriodEnvelope(envelope: unknown) {
  const response = await fetch(`${expressURL()}/api/crypto/access-period-envelope`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders().headers
    },
    body: JSON.stringify({ envelope })
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to store this week\'s court-access period envelope');
  }
  return response.json() as Promise<{ periodId: string; envelope: unknown }>;
}

async function persistCryptoSetup(body: object) {
  const response = await authFetch(`${expressURL()}/auth/crypto/setup`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to save encryption setup');
  }
  return response.json();
}

export function CryptoProvider({ children }) {
  const { activeUser, fetchUser } = useUser();
  const [status, setStatus] = useState<CryptoStatus>('idle');
  const [material, setMaterial] = useState<UserCryptoMaterial | null>(null);
  const [identity, setIdentity] = useState<Pick<IdentityBundle, 'privateKey' | 'privateKeyJwk' | 'secretD'> | null>(null);
  const [wrapKey, setWrapKey] = useState<CryptoKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vaultEpoch, setVaultEpoch] = useState(0);
  const accessRootRef = useRef<Uint8Array | null>(null);
  const periodLayerRef = useRef<{ periodId: string; pak: Uint8Array } | null>(null);

  const clearAccessSecrets = useCallback(() => {
    accessRootRef.current?.fill(0);
    accessRootRef.current = null;
    periodLayerRef.current?.pak.fill(0);
    periodLayerRef.current = null;
  }, []);

  const applyIdentity = useCallback((
    opened: Pick<IdentityBundle, 'privateKey' | 'privateKeyJwk' | 'secretD'>,
    wrap: CryptoKey | null,
    wrapRaw: Uint8Array | null,
    userId: string
  ) => {
    persistSession(userId, opened.privateKeyJwk, wrapRaw);
    setIdentity(opened);
    setWrapKey(wrap);
    setStatus('unlocked');
    setError(null);
  }, []);

  const ensureAccessRoot = useCallback(async (wrap: CryptoKey, userId: string) => {
    if (accessRootRef.current) return accessRootRef.current;
    const stored = readStoredAccessRoot(userId);
    if (stored) {
      const opened = await unwrapAccessRootForDevice(stored, wrap, userId);
      accessRootRef.current = opened;
      return opened;
    }
    const created = generateAccessRoot();
    const envelope = await wrapAccessRootForDevice(created, wrap, userId);
    writeStoredAccessRoot(userId, envelope);
    accessRootRef.current = created;
    return created;
  }, []);

  const ensurePeriodLayer = useCallback(async (wrap: CryptoKey, userId: string) => {
    let periodId = isoWeekPeriodId();
    try {
      const response = await fetch(`${expressURL()}/api/crypto/access-period-state`, {
        headers: getAuthHeaders().headers
      });
      if (response.ok) {
        const body = await response.json();
        if (typeof body?.currentPeriodId === 'string') {
          periodId = validateIsoWeekPeriodId(body.currentPeriodId);
        }
      }
    } catch {
      // Stay on the local ISO week if the period-state endpoint is unavailable.
    }
    if (periodLayerRef.current?.periodId === periodId) {
      return periodLayerRef.current;
    }
    periodLayerRef.current?.pak.fill(0);
    periodLayerRef.current = null;
    const accessRoot = await ensureAccessRoot(wrap, userId);
    const pak = generatePeriodAccessKey();
    const envelope = await wrapPeriodAccessKey(pak, accessRoot, userId, periodId);
    const stored = await persistPeriodEnvelope(envelope);
    const canonical = stored?.envelope
      ? await unwrapPeriodAccessKey(stored.envelope, accessRoot, { targetUserId: userId, periodId })
      : pak;
    if (canonical !== pak) pak.fill(0);
    periodLayerRef.current = { periodId, pak: canonical };
    return periodLayerRef.current;
  }, [ensureAccessRoot]);

  const lock = useCallback(() => {
    clearSession();
    clearAccessSecrets();
    setIdentity(null);
    setWrapKey(null);
    setError(null);
    setStatus(material?.enabled ? 'locked' : activeUser ? 'none' : 'idle');
  }, [activeUser, clearAccessSecrets, material?.enabled]);

  useEffect(() => {
    let cancelled = false;
    const apply = async () => {
      if (!activeUser?.id) {
        setMaterial(null);
        setIdentity(null);
        setWrapKey(null);
        clearAccessSecrets();
        setStatus('idle');
        return;
      }
      const cryptoMaterial = (activeUser as { crypto?: UserCryptoMaterial }).crypto;
      if (!cryptoMaterial?.enabled) {
        setMaterial(null);
        setIdentity(null);
        const wrapRaw = readSessionWrap(String(activeUser.id));
        if (wrapRaw) {
          writeStored(STORAGE_WRAP, JSON.stringify({
            userId: String(activeUser.id),
            wrapKeyRaw: bytesToBase64(wrapRaw)
          }));
          setWrapKey(await importWrapKey(wrapRaw));
        }
        clearAccessSecrets();
        setStatus('none');
        return;
      }
      setMaterial(cryptoMaterial);
      const stored = readSessionJwk(String(activeUser.id));
      const wrapRaw = readSessionWrap(String(activeUser.id));
      if (stored) {
        try {
          const privateKey = await importPrivateKey(stored);
          const wrap = wrapRaw ? await importWrapKey(wrapRaw) : null;
          if (cancelled) return;
          persistSession(String(activeUser.id), stored, wrapRaw);
          setIdentity({
            privateKey,
            privateKeyJwk: stored,
            secretD: base64UrlToBytes(stored.d || '')
          });
          setWrapKey(wrap);
          try {
            await ensureAccessRoot(wrap, String(activeUser.id));
          } catch {
            accessRootRef.current = null;
          }
          setStatus('unlocked');
          return;
        } catch {
          clearPersistedAdultIdentityJwk();
        }
      }
      setIdentity(null);
      setStatus('locked');
    };
    apply();
    return () => { cancelled = true; };
  }, [activeUser, clearAccessSecrets, ensureAccessRoot, vaultEpoch]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onVaultChanged = (event: Event) => {
      const userId = String(activeUser?.id || '');
      const detail = (event as CustomEvent<{ userId?: string }>).detail;
      if (detail?.userId && userId && String(detail.userId) !== userId) return;
      setVaultEpoch((value) => value + 1);
    };
    window.addEventListener(ADULT_VAULT_CHANGED_EVENT, onVaultChanged);
    return () => window.removeEventListener(ADULT_VAULT_CHANGED_EVENT, onVaultChanged);
  }, [activeUser?.id]);

  const unlockFromKeys = useCallback(async (
    keys: DerivedPasswordKeys,
    cryptoMaterial: UserCryptoMaterial | undefined,
    userId: string
  ) => {
    const persistWrap = () => {
      setWrapKey(keys.wrapKey);
      writeStored(STORAGE_WRAP, JSON.stringify({
        userId,
        wrapKeyRaw: bytesToBase64(keys.wrapKeyRaw)
      }));
    };
    if (!cryptoMaterial?.enabled || !cryptoMaterial.wrappedPrivateKey) {
      persistWrap();
      clearPersistedAdultIdentityJwk();
      setIdentity(null);
      setStatus('none');
      return;
    }
    try {
      const opened = await unwrapPrivateKeyWithWrapKey(keys.wrapKey, cryptoMaterial.wrappedPrivateKey);
      applyIdentity(opened, keys.wrapKey, keys.wrapKeyRaw, userId);
      try {
        await ensureAccessRoot(keys.wrapKey, userId);
      } catch {
        accessRootRef.current = null;
      }
    } catch (err) {
      persistWrap();
      clearPersistedAdultIdentityJwk();
      setIdentity(null);
      setStatus('locked');
      throw err;
    }
  }, [applyIdentity, ensureAccessRoot]);

  const upgradeLegacyLogin = useCallback(async (password: string, userId: string, me: {
    cryptoEnabled?: boolean;
    crypto?: UserCryptoMaterial;
    shortName?: string;
  }) => {
    const kdf = newPasswordKdf();
    const keys = await derivePasswordKeys(password, kdf);
    const body: {
      password: string;
      passwordKdf: PasswordKdfPublic;
      publicKeyJwk?: JsonWebKey;
      wrappedPrivateKey?: WrappedPrivateKey;
    } = { password: keys.authKey, passwordKdf: kdf };
    let created = null;
    if (!me.cryptoEnabled) {
      created = await wrapPrivateKeyWithWrapKey(keys.wrapKey);
      body.publicKeyJwk = created.identity.publicKeyJwk;
      body.wrappedPrivateKey = created.wrappedPrivateKey;
    }
    const response = await authFetch(`${expressURL()}/auth/upgrade-password-split`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to upgrade login keys');
    }
    await response.json().catch(() => ({}));
    markCookieSession(true);
    if (created) {
      applyIdentity(created.identity, keys.wrapKey, keys.wrapKeyRaw, userId);
    } else {
      try {
        await unlockFromKeys(keys, me.crypto, userId);
      } catch {
        writeStored(STORAGE_WRAP, JSON.stringify({
          userId,
          wrapKeyRaw: bytesToBase64(keys.wrapKeyRaw)
        }));
        setWrapKey(keys.wrapKey);
        setStatus('locked');
      }
    }
    await fetchUser();
  }, [applyIdentity, fetchUser, unlockFromKeys]);

  const loginWithPassword = useCallback(async (email: string, password: string) => {
    setStatus('loading');
    setError(null);
    const pre = await fetchPrelogin(email);
    let bodyPassword = password;
    let keys: DerivedPasswordKeys | null = null;
    const isSplit = pre && (pre as PasswordKdfPublic).v === 1 && (pre as PasswordKdfPublic).salt;
    if (isSplit) {
      keys = await derivePasswordKeys(password, pre as PasswordKdfPublic);
      bodyPassword = keys.authKey;
    }
    await axios.post(`${expressURL()}/auth/login`, { email, password: bodyPassword });
    markCookieSession(true);
    await fetchUser();
    const me = await axios.get(`${expressURL()}/auth/me`, getAuthHeaders());
    const userId = String(me.data.id);
    if (keys) {
      try {
        await unlockFromKeys(keys, me.data.crypto, userId);
      } catch {
        setStatus(me.data.cryptoEnabled ? 'locked' : 'none');
        setError('Logged in, but this password could not unwrap chat keys.');
      }
      return;
    }
    await upgradeLegacyLogin(password, userId, me.data);
  }, [fetchUser, unlockFromKeys, upgradeLegacyLogin]);

  const unlockWithPassword = useCallback(async (password: string) => {
    const email = activeUser?.email;
    if (!email || !activeUser?.id) throw new Error('Not logged in');
    setStatus('loading');
    setError(null);
    const pre = await fetchPrelogin(email);
    if (!pre || (pre as PasswordKdfPublic).v !== 1) {
      setStatus('locked');
      throw new Error('This account still needs a fresh login to set up split keys.');
    }
    const keys = await derivePasswordKeys(password, pre as PasswordKdfPublic);
    try {
      await axios.post(`${expressURL()}/auth/login`, {
        email,
        password: keys.authKey
      });
      markCookieSession(true);
    } catch {
      setStatus(material?.enabled ? 'locked' : 'none');
      const message = 'Could not unlock. Check your login password.';
      setError(message);
      throw new Error(message);
    }
    try {
      await unlockFromKeys(keys, (activeUser as { crypto?: UserCryptoMaterial }).crypto, String(activeUser.id));
    } catch {
      setStatus('locked');
      const message = 'Password is correct, but it cannot unwrap the stored chat key. That key was wrapped with a different secret. Create new chat keys on Settings, or complete Recovery circle.';
      setError(message);
      throw new Error(message);
    }
  }, [activeUser, material?.enabled]);

  const setupEncryption = useCallback(async () => {
    if (!activeUser?.shortName || !activeUser?.id) throw new Error('Not logged in');
    let wrap = wrapKey;
    let wrapRaw = readSessionWrap(String(activeUser.id));
    if (!wrap && wrapRaw) wrap = await importWrapKey(wrapRaw);
    if (!wrap || !wrapRaw) throw new Error('Enter your login password first so keys can be created on this device.');
    setStatus('loading');
    setError(null);
    try {
      let opened = identity;
      let wrappedPrivateKey = opened ? material?.wrappedPrivateKey : undefined;
      let publicKeyJwk = opened ? material?.publicKeyJwk : undefined;
      if (!opened || !wrappedPrivateKey || !publicKeyJwk) {
        const created = await wrapPrivateKeyWithWrapKey(wrap);
        opened = created.identity;
        wrappedPrivateKey = created.wrappedPrivateKey;
        publicKeyJwk = created.identity.publicKeyJwk;
      }
      const saved = await persistCryptoSetup({
        wrap: 'login-hkdf-v1',
        publicKeyJwk,
        wrappedPrivateKey
      });
      applyIdentity(opened, wrap, wrapRaw, String(activeUser.id));
      await ensureAccessRoot(wrap, String(activeUser.id));
      setMaterial(saved.crypto || {
        enabled: true,
        publicKeyJwk,
        wrappedPrivateKey
      });
      await fetchUser();
    } catch (err) {
      setStatus(identity ? 'unlocked' : 'none');
      const message = err instanceof Error ? err.message : 'Setup failed';
      setError(message);
      throw err;
    }
  }, [activeUser, applyIdentity, ensureAccessRoot, fetchUser, identity, material, wrapKey]);

  const changeLoginPassword = useCallback(async (currentPassword: string, newPassword: string) => {
    if (!activeUser?.email || !activeUser?.id) throw new Error('Not logged in');
    const pre = await fetchPrelogin(activeUser.email);
    let currentAuth = currentPassword;
    let oldWrap: CryptoKey | null = wrapKey;
    if (pre && (pre as PasswordKdfPublic).v === 1) {
      const oldKeys = await derivePasswordKeys(currentPassword, pre as PasswordKdfPublic);
      currentAuth = oldKeys.authKey;
      oldWrap = oldKeys.wrapKey;
    }
    const nextKdf = newPasswordKdf();
    const nextKeys = await derivePasswordKeys(newPassword, nextKdf);
    let wrappedPrivateKey: WrappedPrivateKey | undefined;
    let publicKeyJwk = material?.publicKeyJwk;
    if (identity && oldWrap) {
      wrappedPrivateKey = await rewrapPrivateJwk(nextKeys.wrapKey, identity.privateKeyJwk);
    } else if (material?.enabled && material.wrappedPrivateKey && oldWrap) {
      const opened = await unwrapPrivateKeyWithWrapKey(oldWrap, material.wrappedPrivateKey);
      wrappedPrivateKey = await rewrapPrivateJwk(nextKeys.wrapKey, opened.privateKeyJwk);
      publicKeyJwk = material.publicKeyJwk;
      applyIdentity(opened, nextKeys.wrapKey, nextKeys.wrapKeyRaw, String(activeUser.id));
    }
    await axios.post(
      `${expressURL()}/auth/change-password`,
      {
        currentPassword: currentAuth,
        newPassword: nextKeys.authKey,
        passwordKdf: nextKdf,
        wrappedPrivateKey,
        publicKeyJwk
      },
      getAuthHeaders()
    );
    markCookieSession(true);
    if (identity) {
      applyIdentity(identity, nextKeys.wrapKey, nextKeys.wrapKeyRaw, String(activeUser.id));
    } else {
      writeStored(STORAGE_WRAP, JSON.stringify({
        userId: String(activeUser.id),
        wrapKeyRaw: bytesToBase64(nextKeys.wrapKeyRaw)
      }));
      setWrapKey(nextKeys.wrapKey);
    }
    if (oldWrap) {
      try {
        const root = await ensureAccessRoot(oldWrap, String(activeUser.id));
        const envelope = await wrapAccessRootForDevice(root, nextKeys.wrapKey, String(activeUser.id));
        writeStoredAccessRoot(String(activeUser.id), envelope);
      } catch {
        accessRootRef.current = null;
      }
    }
  }, [activeUser, applyIdentity, ensureAccessRoot, identity, material, wrapKey]);

  const wrapThreadKeyForMembers = useCallback(async (
    threadKeyBytes: Uint8Array,
    members: { id: string; cryptoPublicKey?: JsonWebKey | null }[]
  ) => {
    const wrappedKeys: WrappedThreadKey[] = [];
    for (const member of members) {
      if (!member.cryptoPublicKey) {
        throw new Error('Every participant needs encryption set up');
      }
      wrappedKeys.push(await wrapThreadKeyForUser(threadKeyBytes, String(member.id), member.cryptoPublicKey));
    }
    return wrappedKeys;
  }, []);

  const wrapNewThreadKeys = useCallback(async (
    members: { id: string; cryptoPublicKey?: JsonWebKey | null }[],
    options?: { roomType?: string }
  ) => {
    const threadKeyBytes = await generateThreadKeyBytes();
    const wrappedKeys = await wrapThreadKeyForMembers(threadKeyBytes, members);
    const accessPeriodWraps: AccessThreadEpochWrap[] = [];
    if (options?.roomType === ROOM_TYPES.ADULT_PRIVATE) {
      if (!wrapKey || !activeUser?.id) {
        throw new Error('Unlock encryption before starting an encrypted conversation.');
      }
      const layer = await ensurePeriodLayer(wrapKey, String(activeUser.id));
      accessPeriodWraps.push(await wrapThreadEpochForPeriod(threadKeyBytes, layer.pak, {
        targetUserId: String(activeUser.id),
        periodId: layer.periodId
      }));
    }
    return { threadKeyBytes, wrappedKeys, accessPeriodWraps };
  }, [activeUser, ensurePeriodLayer, wrapKey, wrapThreadKeyForMembers]);

  const unwrapThreadKeyForSelf = useCallback(async (wrappedKeys: WrappedThreadKey[]) => {
    if (!identity || !activeUser?.id) throw new Error('Log in again to unlock this thread');
    const mine = wrappedKeys.find((w) => String(w.userId) === String(activeUser.id));
    if (!mine) throw new Error('No wrapped key for this account in the thread');
    return unwrapThreadKey(identity.privateKey, mine);
  }, [activeUser, identity]);

  const exportAdultVaultKey = useCallback(async (ownerUserId: string) => {
    if (!activeUser?.id) {
      throw new Error('Adult-chat keys cannot be wrapped until this account is signed in.');
    }
    return exportAdultVaultFromIdentity({
      ownerUserId,
      expectedUserId: String(activeUser.id),
      expectedPublicKeyJwk: material?.publicKeyJwk,
      identity: identity
        ? {
            userId: String(activeUser.id),
            privateKeyJwk: identity.privateKeyJwk,
            privateKey: identity.privateKey,
            secretD: identity.secretD,
          }
        : null,
    });
  }, [activeUser, identity, material?.publicKeyJwk]);

  const installRecoveredAdultVault = useCallback(async (ownerUserId: string, vaultKey: Uint8Array) => {
    if (!activeUser?.id) {
      throw new Error('Adult-chat keys cannot be installed until this account is signed in.');
    }
    const userId = String(activeUser.id);
    const wrapRaw = readSessionWrap(userId);
    const wrap = wrapKey || (wrapRaw ? await importWrapKey(wrapRaw) : null);
    const opened = await installAdultVaultKey({
      ownerUserId,
      expectedUserId: userId,
      expectedPublicKeyJwk: material?.publicKeyJwk,
      vaultKey,
    });
    applyIdentity(opened, wrap, wrapRaw, userId);
  }, [activeUser, applyIdentity, material?.publicKeyJwk, wrapKey]);

  const exportAccessRootForCustody = useCallback(async () => {
    if (!wrapKey || !activeUser?.id) {
      throw new Error('Unlock encryption before depositing court-access escrow shares.');
    }
    const root = await ensureAccessRoot(wrapKey, String(activeUser.id));
    return new Uint8Array(root);
  }, [activeUser, ensureAccessRoot, wrapKey]);

  const value = useMemo(() => ({
    status,
    material,
    error,
    loginWithPassword,
    unlockWithPassword,
    lock,
    setupEncryption,
    changeLoginPassword,
    wrapNewThreadKeys,
    wrapThreadKeyForMembers,
    unwrapThreadKeyForSelf,
    exportAdultVaultKey,
    installRecoveredAdultVault,
    exportAccessRootForCustody
  }), [
    changeLoginPassword,
    error,
    exportAccessRootForCustody,
    exportAdultVaultKey,
    installRecoveredAdultVault,
    lock,
    loginWithPassword,
    material,
    setupEncryption,
    status,
    unlockWithPassword,
    unwrapThreadKeyForSelf,
    wrapNewThreadKeys,
    wrapThreadKeyForMembers
  ]);

  return (
    <CryptoContext.Provider value={value}>
      {children}
    </CryptoContext.Provider>
  );
}
